import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import {
  GetHohishesHohishesGetData,
  GetHohishHohishesHohishUuidGetData,
  GetPostingsHohishesHohishUuidPostingsGetData,
  HttpValidationError,
} from "@/codegen/openapi2";
import { getJobsHohishesJobsGetQueryKey } from "@/codegen/openapi2/@tanstack/react-query.gen";
import { type Options } from "@/codegen/openapi2/client/types.gen";

import { TWENTY_FOUR_STALE_WHILE_REVALIDATE } from "../react-query";

import {
  getEnrichedHohishesQueryFn,
  getEnrichedHohishPostingsQueryFn,
  getEnrichedHohishQueryFn,
  getJobsQueryFn,
} from "./standalone-fetches";
import { EnrichedHohishPostingType, EnrichedHohishType } from "./types";

// Jobs
export const useGetJobs = () =>
  useQuery({
    queryKey: getJobsHohishesJobsGetQueryKey(),
    queryFn: getJobsQueryFn,
    ...TWENTY_FOUR_STALE_WHILE_REVALIDATE,
  });

// Multiple hohishes
export const useGetEnrichedHohishes = (
  clientOptions: Options<GetHohishesHohishesGetData, true>,
  queryKey: string[],
  options?: Omit<UseQueryOptions<EnrichedHohishType[], HttpValidationError>, "queryKey" | "queryFn">
) =>
  useQuery<EnrichedHohishType[], HttpValidationError>({
    queryKey: [
      "GetEnrichedHohishes",
      clientOptions.query?.legal_entity_uuids || "all",
      ...queryKey,
    ],
    queryFn: () => getEnrichedHohishesQueryFn(clientOptions),
    ...options,
  });

// Single hohish
export const getEnrichedHohishKey = (hohishUuid: string, queryKey: string[]) => [
  "GetEnrichedHohish",
  hohishUuid,
  ...queryKey,
];

export const useGetEnrichedHohish = (
  clientOptions: Options<GetHohishHohishesHohishUuidGetData, true>,
  queryKey: string[]
) =>
  useQuery<EnrichedHohishType, HttpValidationError>({
    queryKey: getEnrichedHohishKey(clientOptions.path.hohish_uuid, queryKey),
    queryFn: () => getEnrichedHohishQueryFn(clientOptions),
  });

export const useGetEnrichedHohishPostings = (
  clientOptions: Options<GetPostingsHohishesHohishUuidPostingsGetData, true>,
  queryKey: string[],
  options?: Omit<
    UseQueryOptions<EnrichedHohishPostingType[], HttpValidationError>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery<EnrichedHohishPostingType[], HttpValidationError>({
    queryKey: ["GetEnrichedHohishPostings", clientOptions.path.hohish_uuid, ...queryKey],
    queryFn: () => getEnrichedHohishPostingsQueryFn(clientOptions),
    ...options,
  });
